import { LobbyTimers, LobbyTimersUi, LobbyTimerUi } from '@/model/LobbyTimers'
import { Commit, Dispatch } from 'vuex'

// dev flag to manually disable the timer (otherwise I had too much logging in the console)
const disableTime = false
/**
 * Template object used at creation of timer objects
 */
const statusTemplate: LobbyTimers = {
  tournamentId: null,
  secondsToStart: -1,
  startInterval: -1,
  secondsToEnd: -1,
  endInterval: -1,
}

/**
 * Filter specific timer object out of given state by corresponding tournament id
 * @param state
 * @param tournamentId
 */
const getLobbyStatusById = (state: LobbyTimers[], tournamentId: string): LobbyTimers => {
  const s = state.filter((status) => tournamentId === status.tournamentId)[0]
  return s
}

/**
 * Converts total amount of remaining seconds into countdown object described by days, hours, minutes and seconds
 * for better read- and usability in frontend components
 * @param totalSecs - Total amount of seconds until an event (lobby opens / closes) happens
 */
export const secondsToLobbyCountdown = (totalSecs: number): LobbyTimerUi => {
  const seconds = Math.floor(totalSecs % 60)
  const minutes = Math.floor((totalSecs / 60) % 60)
  const hours = Math.floor((totalSecs / (60 * 60)) % 24)
  const days = Math.floor(totalSecs / (60 * 60 * 24))
  return {
    days,
    hours,
    minutes,
    seconds,
    totalSecs,
  }
}

const state: LobbyTimers[] = []

const getters = {
  all:
    (state: LobbyTimers[]) =>
    (tournamentId: string): LobbyTimersUi => ({
      start: getters.startCountdown(state)(tournamentId),
      end: getters.endCountdown(state)(tournamentId),
    }),
  startCountdown:
    (state: LobbyTimers[]) =>
    (tournamentId: string): LobbyTimerUi => {
      const totalSecs = getLobbyStatusById(state, tournamentId).secondsToStart
      return secondsToLobbyCountdown(totalSecs)
    },
  endCountdown:
    (state: LobbyTimers[]) =>
    (tournamentId: string): LobbyTimerUi => {
      const totalSecs = getLobbyStatusById(state, tournamentId).secondsToEnd
      return secondsToLobbyCountdown(totalSecs)
    },
  startTimerId:
    (state: LobbyTimers[]) =>
    (tournamentId: string): number =>
      getLobbyStatusById(state, tournamentId).startInterval,
  endTimerId:
    (state: LobbyTimers[]) =>
    (tournamentId: string): number =>
      getLobbyStatusById(state, tournamentId).endInterval,
}

const actions = {
  invokeStartTimer(
    { state, commit, dispatch, getters }: { state: LobbyTimers[]; commit: Commit; dispatch: Dispatch; getters: any },
    tournamentId: string
  ) {
    const status = getLobbyStatusById(state, tournamentId)
    if (disableTime) return
    if (status.secondsToStart > 0) {
      const startInterval = window.setInterval(() => {
        const countdown = getters.startCountdown(tournamentId).totalSecs
        if (countdown <= 0) {
          window.clearInterval(getters.startTimerId(tournamentId))
          commit('setStartTimer', { tournamentId, timer: -1 })
          dispatch('invokeEndTimer', tournamentId)
          return
        } else {
          commit('setStartCountdown', { tournamentId, seconds: countdown - 1 })
        }
      }, 1000)
      commit('setStartInterval', { startInterval, tournamentId })
    } else {
      dispatch('invokeEndTimer', tournamentId)
    }
  },
  invokeEndTimer(
    { state, commit, getters }: { state: LobbyTimers[]; commit: any; getters: any },
    tournamentId: string
  ) {
    const status = getLobbyStatusById(state, tournamentId)
    if (disableTime) return
    if (status.secondsToEnd > -1) {
      const endInterval = window.setInterval(() => {
        const countdown = getters.endCountdown(tournamentId).totalSecs
        if (countdown <= 0) {
          window.clearInterval(getters.endTimerId(tournamentId))
          commit('setEndTimer', { tournamentId, timer: -1 })
          return
        } else {
          commit('setEndCountdown', { tournamentId, seconds: countdown - 1 })
        }
      }, 1000)
      commit('setEndInterval', { endInterval, tournamentId })
    }
  },
  stopAllTimers({ state, commit }: { state: LobbyTimers[]; commit: Commit }) {
    state.forEach((timer) => {
      if (timer.startInterval !== -1) {
        window.clearInterval(timer.startInterval)
        commit('setStartTimer', { tournamentId: timer.tournamentId, timer: -1 })
      }
      if (timer.endInterval !== -1) {
        window.clearInterval(timer.endInterval)
        commit('setStartTimer', { tournamentId: timer.tournamentId, timer: -1 })
      }
    })
  },
}

const mutations = {
  setEndInterval(state: LobbyTimers[], { endInterval, tournamentId }: { endInterval: number; tournamentId: string }) {
    const status = getLobbyStatusById(state, tournamentId)
    status.endInterval = endInterval
  },
  setStartInterval(
    state: LobbyTimers[],
    { startInterval, tournamentId }: { startInterval: number; tournamentId: string }
  ) {
    const status = getLobbyStatusById(state, tournamentId)
    status.startInterval = startInterval
  },
  setState(
    state: LobbyTimers[],
    {
      tournamentId,
      secondsToStart,
      secondsToEnd,
    }: { tournamentId: string; secondsToStart: number; secondsToEnd: number }
  ) {
    const existingTimer = getLobbyStatusById(state, tournamentId)
    if (existingTimer) {
      existingTimer.secondsToStart = secondsToStart
      existingTimer.secondsToEnd = secondsToEnd
    } else {
      state.push({ ...statusTemplate, tournamentId, secondsToStart, secondsToEnd })
    }
  },
  setStartCountdown(state: LobbyTimers[], { tournamentId, seconds }: { tournamentId: string; seconds: number }) {
    const status = getLobbyStatusById(state, tournamentId)
    status.secondsToStart = seconds
  },
  setEndCountdown(state: LobbyTimers[], { tournamentId, seconds }: { tournamentId: string; seconds: number }) {
    const status = getLobbyStatusById(state, tournamentId)
    status.secondsToEnd = seconds
  },
  setStartTimer(state: LobbyTimers[], { tournamentId, timer }: { tournamentId: string; timer: number }) {
    const status = getLobbyStatusById(state, tournamentId)
    status.startInterval = timer
  },
  setEndTimer(state: LobbyTimers[], { tournamentId, timer }: { tournamentId: string; timer: number }) {
    const status = getLobbyStatusById(state, tournamentId)
    status.endInterval = timer
  },
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
